/**
 * The mutually-exclusive top-level surfaces the app can show. Derived from the raw auth facts by
 * {@link selectView} so the render decision lives in one tested place, not scattered across JSX.
 * The in-flight login state is owned by the shared login form itself (its own submit spinner), so
 * the app only tracks the coarse surface: discovering → login → authenticated (or unavailable).
 */
export type AppView = 'authenticated' | 'discovering' | 'login' | 'unavailable'

export type AuthAction =
  | { authenticated: boolean; type: 'session-resolved' }
  | { available: boolean; type: 'runtime-resolved' }
  | { type: 'login-succeeded' }

/**
 * Raw auth facts the app tracks, kept deliberately separate from the derived {@link AppView}:
 *  - `session` — the result of the local IndexedDB token read (`unknown` until it resolves);
 *  - `runtime` — whether boot-time discovery adopted a signature-verified live domain.
 */
export interface AuthState {
  runtime: 'pending' | 'ready' | 'unavailable'
  session: 'anonymous' | 'authenticated' | 'unknown'
}

export const initialAuthState: AuthState = {
  runtime: 'pending',
  session: 'unknown',
}

/** Fold one auth event into the raw state. All view decisions are deferred to {@link selectView}. */
export const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'login-succeeded':
      return { ...state, session: 'authenticated' }
    case 'runtime-resolved':
      return { ...state, runtime: action.available ? 'ready' : 'unavailable' }
    case 'session-resolved':
      return { ...state, session: action.authenticated ? 'authenticated' : 'anonymous' }
    default:
      return state
  }
}

/**
 * Decide which single surface to render from the raw facts. Precedence is deliberate:
 *  1. a held session always wins — a returning client sees the app immediately, even while (or if)
 *     discovery is still settling, because the bearer token already proves who they are;
 *  2. an unresolved local session keeps the discovering state (the IDB read is quick and local);
 *  3. otherwise the login form appears only once a live domain is adopted, and the unavailable
 *     surface only when discovery has exhausted the window with nothing to trust.
 */
export const selectView = (state: AuthState): AppView => {
  if (state.session === 'authenticated') return 'authenticated'
  if (state.session === 'unknown') return 'discovering'
  if (state.runtime === 'ready') return 'login'
  if (state.runtime === 'unavailable') return 'unavailable'
  return 'discovering'
}
